<template>
  <section class="space-y-5" aria-labelledby="grades-heading">
    <header class="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h3 id="grades-heading" class="text-2xl font-bold text-slate-900 dark:text-white">Grades by category</h3>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">{{ assignments.length }} posted assignments across {{ groups.length }} categories</p>
      </div>
      <div v-if="official !== null" class="text-left sm:text-right">
        <p class="text-xs text-slate-500 dark:text-slate-400">Current grade · Blackbaud</p>
        <p class="text-3xl font-bold tabular-nums text-slate-900 dark:text-white">{{ formatScore(official) }}%</p>
      </div>
    </header>

    <WhatIfCalculator ref="calculator" :assignments="assignments" :official-grade="officialGrade" />

    <div v-if="groups.length" class="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
      <article v-for="group in groups" :key="group.name" class="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        <header class="border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-800/60">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <h4 class="break-words text-lg font-semibold text-slate-900 dark:text-white">{{ group.name }}</h4>
              <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">{{ group.weight !== null ? `${formatScore(group.weight)}% weight` : 'Weight unavailable' }} · {{ group.assignments.length }} assignments</p>
            </div>
            <div class="shrink-0 text-right">
              <p class="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{{ group.average === null ? '—' : `${formatScore(group.average)}%` }}</p>
              <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">{{ group.reported !== null ? 'Blackbaud average' : 'Estimated average' }}</p>
            </div>
          </div>
        </header>
        <ul class="divide-y divide-slate-100 px-5 dark:divide-slate-800">
          <li v-for="assignment in visibleAssignments(group)" :key="assignment.assignment_id" class="flex items-start justify-between gap-4 py-3.5">
            <div class="min-w-0">
              <p class="break-words text-sm leading-relaxed text-slate-800 dark:text-slate-200">{{ assignment.short_description }}</p>
              <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">{{ formatDate(assignment.date_due) }}<span v-if="assignment.exempt"> · Exempt</span><span v-else-if="assignment.formative"> · Formative</span><span v-else-if="group.excluded.includes(assignment)" title="Blackbaud's category average leaves this score out, so the what-if calculator does too."> · Not counted</span></p>
              <p v-if="assignment.comment" class="mt-1.5 whitespace-pre-line break-words border-l-2 border-slate-200 pl-2 text-xs italic leading-relaxed text-slate-600 dark:border-slate-700 dark:text-slate-400">{{ assignment.comment }}</p>
            </div>
            <span class="shrink-0 pt-0.5 text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200" :class="{ 'line-through opacity-50': group.excluded.includes(assignment) }">{{ formatScore(assignment.assignment_percentage) }}%</span>
          </li>
        </ul>
        <footer class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 dark:border-slate-700">
          <button v-if="group.assignments.length > 4" type="button" class="grade-link text-slate-500 dark:text-slate-400" :aria-expanded="!!expanded[group.name]" @click="expanded[group.name] = !expanded[group.name]">{{ expanded[group.name] ? 'Show fewer' : `Show all ${group.assignments.length}` }}</button>
          <span v-else></span>
          <button v-if="group.count" type="button" class="grade-link text-indigo-600 dark:text-indigo-400" :aria-label="`Add what-if assignment to ${group.name}`" @click="calculator.addRow(group.name)">+ Add what-if</button>
        </footer>
      </article>
    </div>
    <p v-else class="py-8 text-center text-sm text-slate-500 dark:text-slate-400">This class does not have any grades published yet.</p>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import WhatIfCalculator from './WhatIfCalculator.vue';
import { categorySummaries, gradeNumber } from '@/utils/whatIfGrades.mjs';
const props = defineProps({ assignments: { type: Array, required: true }, officialGrade: { type: [Number, String], default: null } });
const calculator = ref(null);
const expanded = ref({});
const groups = computed(() => categorySummaries(props.assignments));
const official = computed(() => gradeNumber(props.officialGrade));
const formatScore = value => gradeNumber(value) === null ? '—' : Number(Number(value).toFixed(2)).toString();
function visibleAssignments(group) {
  const sorted = [...group.assignments].sort((a, b) => (Date.parse(b.date_due) || 0) - (Date.parse(a.date_due) || 0));
  return expanded.value[group.name] ? sorted : sorted.slice(0, 4);
}
function formatDate(value) {
  if (!value) return 'No due date';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? 'No due date' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
</script>

<style scoped>
.grade-link { @apply rounded text-xs font-medium hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500; }
</style>
